import CoverImage from 'images/news/blogs/solitaire-terms-explained/cover.jpg'
import BlogImage from 'images/news/blogs/solitaire-terms-explained/img.png'
import BlogImage1 from 'images/news/blogs/solitaire-terms-explained/img_1.png'
import BlogImage2 from 'images/news/blogs/solitaire-terms-explained/img_2.png'
import BlogImage3 from 'images/news/blogs/solitaire-terms-explained/img_3.png'
import BlogImage4 from 'images/news/blogs/solitaire-terms-explained/img_4.png'
import BlogImage5 from 'images/news/blogs/solitaire-terms-explained/img_5.png'

import { SolitaireClashLink, TextLink } from '../../../components'
import { joinDomain } from '../../../utils'
import { GameType } from '../../games'
import { NewsCategory, NewsItem } from '../home'
import {
  SolitaireHorizontalMoveRoute,
  SolitaireMovesCardsRoute,
  SolitaireTermsExplainedRoute,
  SolitaireUndoTrickRoute,
} from '../route'
import { BlogData, BlogModule } from './const'

export const SolitaireTermsExplained: BlogData = {
  key: 'terms-explained',
  title: 'Solitaire Clash Guide 101: Solitaire Card Game Terms Explained',
  tag: NewsCategory.GuidesAndTips,
  date: 'Jun 25, 2025',
  game: GameType.SolitaireClash,
  content: [
    {
      type: BlogModule.Paragraph,
      value: (
        <>
          Welcome to <SolitaireClashLink /> Guide 101—your go-to series for leveling up your game and stacking up those
          wins! Whether you&#39;re a total newbie or a card-flipping pro, we&#39;ve got you covered with tips, clever
          tricks, and game-changing strategies. From must-know terms to pro-level moves, each post is packed with
          everything you need to play sharper, faster, and better.
          <br />
          <br />
          In this post, we’re starting with the ABCs of Solitaire Clash—the key terms that might sound tricky at first
          but are essential to your game. Don’t worry if words like &#34;<strong>foundation</strong>&#34; or &#34;
          <strong>tableau</strong>&#34; cause you to scratch your head for now, we’ve all been there! But once you learn
          them, you’ll play with way more confidence and strategy. So, let’s get into it!
        </>
      ),
    },
    {
      type: BlogModule.Title,
      value: <>The 4 Types of Piles</>,
    },
    {
      type: BlogModule.Paragraph,
      value: (
        <>
          Every game of solitaire is built on four key pile types. Each one plays a unique role, and when you master
          their flow, winning becomes way easier and way more fun!
        </>
      ),
    },
    {
      type: BlogModule.Subtitle,
      value: (
        <>
          1.<strong>Foundation Piles</strong>
        </>
      ),
    },
    {
      type: BlogModule.Image,
      value: {
        image: BlogImage,
      },
    },
    {
      type: BlogModule.Paragraph,
      value: (
        <>
          Let’s start with the heart of any <strong>solitaire card game</strong>—the <strong>foundation piles</strong>.
          These are your victory zones, where cards are stacked in <strong>ascending order</strong> by suit, from Ace to
          King. The real challenge is unlocking the right cards to build them. Every card you place here is a step
          closer to sweet victory!
        </>
      ),
    },
    {
      type: BlogModule.Subtitle,
      value: (
        <>
          2.<strong>Tableau Piles</strong>
        </>
      ),
    },
    {
      type: BlogModule.Image,
      value: {
        image: BlogImage1,
      },
    },
    {
      type: BlogModule.Paragraph,
      value: (
        <>
          The <strong>tableau</strong> is where the action happens. It’s the heart of your{' '}
          <strong>solitaire setup</strong>! You’ll see seven columns stacked like a staircase, with cards you can move
          and hidden ones to uncover. Following <strong>solitaire rules</strong>, face-up cards in these stacks can be
          moved in <strong>descending order</strong>, and alternate between red and black.
        </>
      ),
    },
    {
      type: BlogModule.Subtitle,
      value: (
        <>
          3.<strong>Stock Pile</strong>
        </>
      ),
    },
    {
      type: BlogModule.Image,
      value: {
        image: BlogImage2,
      },
    },
    {
      type: BlogModule.Paragraph,
      value: (
        <>
          Oh no! Did you run out of moves in the <strong>tableau</strong>? Don&#39;t worry! The{' '}
          <strong>stock pile</strong> is here to rescue you. This is the deck you draw undealt cards from, and is
          usually tucked away in the top-right corner. Flip cards from here three at a time to reveal fresh cards and
          keep your game going!
        </>
      ),
    },
    {
      type: BlogModule.Subtitle,
      value: (
        <>
          4.<strong>Waste Pile</strong>
        </>
      ),
    },
    {
      type: BlogModule.Image,
      value: {
        image: BlogImage3,
      },
    },
    {
      type: BlogModule.Paragraph,
      value: (
        <>
          The <strong>Waste Pile</strong> is where cards from the <strong>Stock Pile</strong> go when they&#39;re
          revealed—three-at-a-time, face-up and ready for a second chance! Only the top card of the flipped cards can be
          moved, reveal the bottom ones by moving the cards above it. Managing the <strong>waste pile</strong> is an
          essential part of any <strong>solitaire strategy</strong>. You never know when that hidden gem will pop up and
          turn the game in your favor!
        </>
      ),
    },
    {
      type: BlogModule.Title,
      value: <>Navigating Empty Columns</>,
    },
    {
      type: BlogModule.Image,
      value: {
        image: BlogImage4,
      },
    },
    {
      type: BlogModule.Paragraph,
      value: (
        <>
          Now that you’ve nailed the four piles, here’s a power tip for <strong>Klondike Solitaire</strong>: empty
          columns are golden! Only a King (plus its stacked pals) can be moved into these spaces, so use them wisely.
          It’s a smart way to shake up the board and flip those tricky face-down cards.
        </>
      ),
    },
    {
      type: BlogModule.Title,
      value: (
        <>
          The Magic of the <strong>Undo Button</strong>
        </>
      ),
    },
    {
      type: BlogModule.Image,
      value: {
        image: BlogImage5,
      },
    },
    {
      type: BlogModule.Paragraph,
      value: (
        <>
          And finally—the ultimate game-changer: the <strong>undo button</strong>. Made a move you regret? Tap undo and
          take it back! It’s your no-stress, no-penalty second chance to rethink, retry, and keep your winning streak
          alive.
        </>
      ),
    },
    {
      type: BlogModule.Title,
      value: <>Master Solitaire Clash Guide 101</>,
    },
    {
      type: BlogModule.Paragraph,
      value: (
        <>
          Congrats, card champ—you’ve just taken your first big step toward mastering Solitaire Clash! From
          understanding the lingo to clearing the <strong>solitaire tableau</strong> and knowing exactly when to hit
          that <strong>undo button</strong>, you’re no longer just playing—you’re outsmarting the deck like a true{' '}
          <SolitaireClashLink>Klondike Solitaire</SolitaireClashLink> strategist.
          <br />
          <br />
          The fun doesn’t stop here! Up next in the Solitaire Clash Guide 101 series, we’re diving even deeper with more
          tips to level up your game. Learn how to master the{' '}
          <TextLink href={SolitaireUndoTrickRoute}>undo trick</TextLink> for smarter wins,{' '}
          <TextLink href={SolitaireMovesCardsRoute}>move cards</TextLink> down from the foundation, and use{' '}
          <TextLink href={SolitaireHorizontalMoveRoute}>horizontal moves</TextLink> to unlock new cards. Exciting times
          ahead—get Solitaire Clash today and play with confidence for opportunities to win real cash prizes!
        </>
      ),
    },
  ],
  more: [],
  path: joinDomain(SolitaireTermsExplainedRoute),
}

export const SolitaireTermsExplainedNews: NewsItem = {
  id: 13,
  image: CoverImage,
  breadcrumb: [SolitaireTermsExplained.tag, SolitaireTermsExplained.date],
  title: SolitaireTermsExplained.title,
  url: SolitaireTermsExplained.path,
}
